import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getFeatureFlags, updateFeatureFlag, updateTabOrder as updateTabOrderApi } from '../auth/api';
import { useAuth } from '../auth/useAuth';

const FeatureFlagsContext = createContext(null);

export function FeatureFlagsProvider({ children }) {
  const { user } = useAuth();
  const [flags, setFlags] = useState({});
  const [loaded, setLoaded] = useState(false);

  const loadFlags = useCallback(async () => {
    try {
      const data = await getFeatureFlags();
      if (data && typeof data === 'object') {
        setFlags(data);
        setLoaded(true);
      }
    } catch {
      // Si falla la carga, todas las funciones permanecen habilitadas (fail-open)
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (user) loadFlags();
  }, [user, loadFlags]);

  /**
   * Devuelve true si la función está habilitada.
   * Si los flags aún no se han cargado o la clave no existe, devuelve true (fail-open).
   */
  const isEnabled = useCallback((key) => {
    if (!loaded || !(key in flags)) return true;
    return flags[key].enabled === true;
  }, [flags, loaded]);

  const toggleFlag = useCallback(async (key, enabled) => {
    await updateFeatureFlag(key, enabled);
    setFlags(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled },
    }));
  }, []);

  const updateTabOrder = useCallback(async (orderedKeys) => {
    await updateTabOrderApi(orderedKeys);
    setFlags(prev => {
      const next = { ...prev };
      orderedKeys.forEach((key, idx) => {
        if (next[key]) next[key] = { ...next[key], sort_order: idx + 1 };
      });
      return next;
    });
  }, []);

  return (
    <FeatureFlagsContext.Provider value={{ flags, loaded, isEnabled, toggleFlag, updateTabOrder, reloadFlags: loadFlags }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
}
